/**
 * Extracts a user-friendly error message from an Axios error
 * @param error - The error object from a catch block
 * @param fallbackMessage - Default message if extraction fails
 * @returns User-friendly error message
 */
export const extractErrorMessage = (error: any, fallbackMessage: string = 'An error occurred'): string => {
  // Try to extract from errors array with detail field (API standard format)
  if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
    const errorMessages = error.response.data.errors
      .map((e: any) => {
        // Try detail first (most specific)
        if (e.detail) return e.detail;
        // Try title (less specific but still useful)
        if (e.title) return e.title;
        // Try message field
        if (e.message) return e.message;
        // Try the error object itself if it's a string
        if (typeof e === 'string') return e;
        return null;
      })
      .filter(Boolean); // Remove nulls

    if (errorMessages.length > 0) {
      return errorMessages.join('; ');
    }
  }

  // Try to extract API error message (alternative format)
  if (error.response?.data?.message) {
    return error.response.data.message;
  }

  // Try generic response data string
  if (error.response?.data && typeof error.response.data === 'string') {
    return error.response.data;
  }

  // Network errors or other axios errors
  if (error.message) {
    // For network errors, provide more context
    if (error.message === 'Network Error') {
      return 'Unable to connect to server. Please check your connection and try again.';
    }
    return error.message;
  }

  // Ultimate fallback
  return fallbackMessage;
};

/**
 * Determines if an error is a network error (no response from server)
 */
export const isNetworkError = (error: any): boolean => {
  return error.message === 'Network Error' || !error.response;
};

/**
 * Gets HTTP status code from error, or null if not available
 */
export const getErrorStatusCode = (error: any): number | null => {
  return error.response?.status || null;
};
